import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Modal,
  Card,
  ProgressBar,
  Badge,
  Button,
  Spinner,
  Collapse,
  Alert,
  Table
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlay,
  faTimes,
  faCheck,
  faSpinner,
  faExclamationTriangle,
  faImage,
  faChevronDown,
  faChevronUp,
  faUpload,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import { db } from 'config/firebase';
import { doc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { ManualImageUploadRow } from './ManualImageUpload';
import { ImageSourceBadge } from './ImageSourceBadge';
import './ImageSourcingPanel.css';

// Cloud Functions URL for API calls
const CLOUD_FUNCTIONS_URL = process.env.REACT_APP_CLOUD_FUNCTIONS_URL || 'https://us-central1-stk-merlin.cloudfunctions.net';

/**
 * Job status constants matching backend.
 */
const JOB_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  PARTIAL: 'partial',
  CANCELLED: 'cancelled',
  FAILED: 'failed'
};

/**
 * Item status constants.
 */
const ITEM_STATUS = {
  QUEUED: 'queued',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  NEEDS_MANUAL: 'needs_manual',
  FAILED: 'failed'
};

/**
 * Status configuration for badges and display.
 */
const STATUS_CONFIG = {
  [JOB_STATUS.PENDING]: { variant: 'secondary', icon: faClock, label: 'Pending' },
  [JOB_STATUS.PROCESSING]: { variant: 'primary', icon: faSpinner, label: 'Processing', spin: true },
  [JOB_STATUS.COMPLETED]: { variant: 'success', icon: faCheck, label: 'Completed' },
  [JOB_STATUS.PARTIAL]: { variant: 'warning', icon: faExclamationTriangle, label: 'Partial' },
  [JOB_STATUS.CANCELLED]: { variant: 'danger', icon: faTimes, label: 'Cancelled' },
  [JOB_STATUS.FAILED]: { variant: 'danger', icon: faTimes, label: 'Failed' }
};

const ITEM_STATUS_CONFIG = {
  [ITEM_STATUS.QUEUED]: { variant: 'secondary', icon: faClock, label: 'Queued' },
  [ITEM_STATUS.PROCESSING]: { variant: 'primary', icon: faSpinner, label: 'Scraping', spin: true },
  [ITEM_STATUS.COMPLETED]: { variant: 'success', icon: faCheck, label: 'Found' },
  [ITEM_STATUS.NEEDS_MANUAL]: { variant: 'warning', icon: faUpload, label: 'Needs Manual' },
  [ITEM_STATUS.FAILED]: { variant: 'danger', icon: faTimes, label: 'Failed' }
};

/**
 * ImageSourcingPanel - Modal panel for image sourcing workflow.
 *
 * Features:
 * - Create job with selected SKUs
 * - Real-time Firestore listener for job progress
 * - Real-time Firestore listener for job items
 * - Start/Cancel/Close buttons
 * - Stats grid: Sourced / Needs Manual / Failed
 * - "Needs Manual" section with ManualImageUpload for each failed product
 *
 * @param {boolean} show - Whether modal is visible
 * @param {function} onClose - Close handler
 * @param {string[]} selectedSkus - Array of SKUs to process
 * @param {object[]} selectedProducts - Array of product objects for display
 */
const ImageSourcingPanel = ({ show, onClose, selectedSkus = [], selectedProducts = [] }) => {
  // Job state
  const [job, setJob] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // UI state
  const [showDetails, setShowDetails] = useState(false);
  const [showNeedsManual, setShowNeedsManual] = useState(true);
  const [processingRef, setProcessingRef] = useState(null);

  // Unsubscribe refs for Firestore listeners
  const jobUnsubscribeRef = useRef(null);
  const itemsUnsubscribeRef = useRef(null);

  /**
   * Clean up Firestore listeners on unmount or modal close.
   */
  const cleanupListeners = useCallback(() => {
    if (jobUnsubscribeRef.current) {
      jobUnsubscribeRef.current();
      jobUnsubscribeRef.current = null;
    }
    if (itemsUnsubscribeRef.current) {
      itemsUnsubscribeRef.current();
      itemsUnsubscribeRef.current = null;
    }
  }, []);

  /**
   * Reset state when modal closes.
   */
  useEffect(() => {
    if (!show) {
      cleanupListeners();
      // Don't reset job state immediately - allow user to see results if they reopen
    }
    return cleanupListeners;
  }, [show, cleanupListeners]);

  /**
   * Set up Firestore listener for job document.
   */
  const setupJobListener = useCallback((jobId) => {
    // Clean up existing listener
    if (jobUnsubscribeRef.current) {
      jobUnsubscribeRef.current();
    }

    const jobRef = doc(db, 'productCatalog/topicz/imageSourcingJobs', jobId);
    jobUnsubscribeRef.current = onSnapshot(
      jobRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setJob({ id: snapshot.id, ...snapshot.data() });
        }
      },
      (err) => {
        console.error('Error listening to job:', err);
        setError('Failed to listen to job updates');
      }
    );
  }, []);

  /**
   * Set up Firestore listener for job items.
   */
  const setupItemsListener = useCallback((jobId) => {
    // Clean up existing listener
    if (itemsUnsubscribeRef.current) {
      itemsUnsubscribeRef.current();
    }

    const itemsQuery = query(
      collection(db, 'productCatalog/topicz/imageSourcingJobItems'),
      where('jobId', '==', jobId)
    );

    itemsUnsubscribeRef.current = onSnapshot(
      itemsQuery,
      (snapshot) => {
        const itemsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setItems(itemsData);
      },
      (err) => {
        console.error('Error listening to items:', err);
        // Don't set error - items listener is secondary
      }
    );
  }, []);

  /**
   * Create a new image sourcing job.
   */
  const createJob = async () => {
    if (!selectedSkus || selectedSkus.length === 0) {
      toast.error('No products selected', { theme: 'colored' });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${CLOUD_FUNCTIONS_URL}/imageSourcingCreate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          skus: selectedSkus,
          config: {
            minWidth: 400,
            minHeight: 400,
            maxImages: 5,
            validateImages: true
          }
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create job');
      }

      // Set up listeners for the new job
      setJob(data.job);
      setupJobListener(data.job.id);
      setupItemsListener(data.job.id);

      toast.success(`Image sourcing job created for ${selectedSkus.length} products`, { theme: 'colored' });
    } catch (err) {
      console.error('Error creating job:', err);
      setError(err.message);
      toast.error(`Failed to create job: ${err.message}`, { theme: 'colored' });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Start processing the job.
   */
  const processJob = async () => {
    if (!job?.id) return;

    setLoading(true);
    setError(null);

    try {
      // Start processing loop
      let continuationToken = null;
      let loopComplete = false;
      let iterationCount = 0;
      const maxIterations = 100; // Safety limit

      while (!loopComplete && iterationCount < maxIterations) {
        iterationCount++;
        console.log(`[ImageSourcing] Processing iteration ${iterationCount}, token: ${continuationToken}`);

        const response = await fetch(`${CLOUD_FUNCTIONS_URL}/imageSourcingProcess`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jobId: job.id,
            continuationToken
          })
        });

        const data = await response.json();
        console.log(`[ImageSourcing] Response:`, data);

        if (!response.ok) {
          throw new Error(data.error || 'Failed to process job');
        }

        if (data.completed) {
          loopComplete = true;
          toast.success('Image sourcing completed', { theme: 'colored' });
        } else if (data.continuationToken) {
          continuationToken = data.continuationToken;
        } else if (data.cancelled) {
          toast.info('Image sourcing was cancelled', { theme: 'colored' });
          break;
        } else {
          // Unexpected state - no continuation token but not complete
          console.warn('[ImageSourcing] Unexpected response state:', data);
          // Break to avoid infinite loop, but don't mark as completed
          toast.warning('Processing stopped unexpectedly. Check console for details.', { theme: 'colored' });
          break;
        }
      }

      if (iterationCount >= maxIterations) {
        console.error('[ImageSourcing] Max iterations reached');
        toast.warning('Processing stopped: too many iterations', { theme: 'colored' });
      }
    } catch (err) {
      console.error('Error processing job:', err);
      setError(err.message);
      toast.error(`Processing failed: ${err.message}`, { theme: 'colored' });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Cancel the current job.
   */
  const cancelJob = async () => {
    if (!job?.id) return;

    setLoading(true);

    try {
      const response = await fetch(`${CLOUD_FUNCTIONS_URL}/imageSourcingCancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobId: job.id })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to cancel job');
      }

      toast.info('Job cancelled', { theme: 'colored' });
    } catch (err) {
      console.error('Error cancelling job:', err);
      toast.error(`Failed to cancel: ${err.message}`, { theme: 'colored' });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Close modal and reset state.
   */
  const handleClose = () => {
    cleanupListeners();
    setJob(null);
    setItems([]);
    setError(null);
    onClose();
  };

  /**
   * Handle manual upload completion.
   */
  const handleManualUpload = (result) => {
    // The item listener will automatically update when Firestore changes
    console.log('Manual upload completed:', result);
  };

  // Calculate progress stats
  // Backend uses: progress.completed, progress.failed, progress.needsManual
  const progress = job?.progress || {};
  const total = progress.total || selectedSkus.length || 0;
  const processed = (progress.completed || 0) + (progress.needsManual || 0) + (progress.failed || 0);
  const percentage = total > 0 ? Math.round((processed / total) * 100) : 0;

  // Determine job status
  const status = job?.status || JOB_STATUS.PENDING;
  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const isProcessing = status === JOB_STATUS.PROCESSING;
  const isComplete = status === JOB_STATUS.COMPLETED || status === JOB_STATUS.PARTIAL;
  const canStart = !job || status === JOB_STATUS.PENDING;
  const canCancel = status === JOB_STATUS.PROCESSING;

  // Filter items needing manual upload
  const needsManualItems = items.filter(item => item.status === ITEM_STATUS.NEEDS_MANUAL);

  // Create a lookup for product details
  const productLookup = {};
  selectedProducts.forEach(p => {
    productLookup[p.sku] = p;
    if (p.parentSku) {
      productLookup[p.parentSku] = p;
    }
  });

  return (
    <Modal show={show} onHide={handleClose} centered size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faImage} className="me-2 text-primary" />
          Source Product Images
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Initial state - no job yet */}
        {!job && (
          <Alert variant="info">
            <FontAwesomeIcon icon={faImage} className="me-2" />
            Ready to source images for <strong>{selectedSkus.length}</strong> product{selectedSkus.length !== 1 ? 's' : ''}.
            <br />
            <small className="text-muted">
              Images will be sourced from brand websites, Amazon, and Google Images.
            </small>
          </Alert>
        )}

        {/* Error display */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            {error}
          </Alert>
        )}

        {/* Job progress card */}
        {job && (
          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <strong>{job.name || 'Image Sourcing Job'}</strong>
                <Badge bg={statusConfig.variant} className="ms-2">
                  <FontAwesomeIcon icon={statusConfig.icon} spin={statusConfig.spin} className="me-1" />
                  {statusConfig.label}
                </Badge>
              </div>
              <small className="text-muted">ID: {job.id?.slice(0, 8)}...</small>
            </Card.Header>

            <Card.Body>
              {/* Progress Bar */}
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span>Progress</span>
                  <span>
                    <strong>{processed}</strong> / {total} ({percentage}%)
                  </span>
                </div>
                <ProgressBar
                  now={percentage}
                  variant={statusConfig.variant}
                  animated={isProcessing}
                />
              </div>

              {/* Stats Grid */}
              <div className="d-flex justify-content-around mb-3 text-center">
                <div>
                  <div className="text-success fw-bold fs-5">{progress.completed || 0}</div>
                  <small className="text-muted">Sourced</small>
                </div>
                <div>
                  <div className="text-warning fw-bold fs-5">{progress.needsManual || 0}</div>
                  <small className="text-muted">Needs Manual</small>
                </div>
                <div>
                  <div className="text-danger fw-bold fs-5">{progress.failed || 0}</div>
                  <small className="text-muted">Failed</small>
                </div>
                <div>
                  <div className="text-secondary fw-bold fs-5">{progress.queued || (total - processed)}</div>
                  <small className="text-muted">Queued</small>
                </div>
              </div>

              {/* Show details toggle */}
              <Button
                variant="link"
                size="sm"
                className="p-0 mb-2"
                onClick={() => setShowDetails(!showDetails)}
              >
                <FontAwesomeIcon icon={showDetails ? faChevronUp : faChevronDown} className="me-1" />
                {showDetails ? 'Hide' : 'Show'} item details ({items.length} items)
              </Button>

              {/* Item details table */}
              <Collapse in={showDetails}>
                <div className="item-details-table" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  <Table size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Status</th>
                        <th>Source</th>
                        <th>Images</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.slice(0, 50).map(item => {
                        const itemConfig = ITEM_STATUS_CONFIG[item.status] || ITEM_STATUS_CONFIG.queued;
                        return (
                          <tr key={item.id} className={item.status === ITEM_STATUS.NEEDS_MANUAL ? 'table-warning' : ''}>
                            <td>
                              <code className="small">{item.sku}</code>
                            </td>
                            <td>
                              <Badge bg={itemConfig.variant} className="badge-sm">
                                <FontAwesomeIcon
                                  icon={itemConfig.icon}
                                  spin={itemConfig.spin}
                                  className="me-1"
                                />
                                {itemConfig.label}
                              </Badge>
                            </td>
                            <td>
                              {item.source && <ImageSourceBadge source={item.source} compact />}
                            </td>
                            <td>
                              {item.imagesFound || 0}
                            </td>
                          </tr>
                        );
                      })}
                      {items.length > 50 && (
                        <tr>
                          <td colSpan="4" className="text-center text-muted">
                            ... and {items.length - 50} more
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Collapse>
            </Card.Body>
          </Card>
        )}

        {/* Needs Manual Section */}
        {needsManualItems.length > 0 && (
          <Card>
            <Card.Header
              className="d-flex justify-content-between align-items-center cursor-pointer"
              onClick={() => setShowNeedsManual(!showNeedsManual)}
            >
              <div>
                <FontAwesomeIcon icon={faUpload} className="me-2 text-warning" />
                <strong>Needs Manual Upload</strong>
                <Badge bg="warning" className="ms-2">{needsManualItems.length}</Badge>
              </div>
              <FontAwesomeIcon icon={showNeedsManual ? faChevronUp : faChevronDown} />
            </Card.Header>

            <Collapse in={showNeedsManual}>
              <Card.Body className="p-0">
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {needsManualItems.map(item => {
                    const product = productLookup[item.sku] || { sku: item.sku, name: item.productName };
                    return (
                      <ManualImageUploadRow
                        key={item.sku}
                        product={product}
                        onUploadComplete={handleManualUpload}
                      />
                    );
                  })}
                </div>
              </Card.Body>
            </Collapse>
          </Card>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          {isComplete ? 'Close' : 'Cancel'}
        </Button>

        {canStart && (
          <Button
            variant="primary"
            onClick={job ? processJob : createJob}
            disabled={loading || selectedSkus.length === 0}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {job ? 'Starting...' : 'Creating Job...'}
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPlay} className="me-2" />
                {job ? 'Start Processing' : `Source ${selectedSkus.length} Products`}
              </>
            )}
          </Button>
        )}

        {canCancel && (
          <Button
            variant="outline-danger"
            onClick={cancelJob}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faTimes} className="me-2" />
            Stop
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ImageSourcingPanel;
export { ImageSourcingPanel, JOB_STATUS, ITEM_STATUS };
